import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import {
  Container, Box, Card, CardContent, Typography, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, Grid, InputAdornment, Alert, useMediaQuery, useTheme, Divider
} from '@mui/material';
import { Add, Edit, Delete, ArrowBack, CheckCircleOutline, RadioButtonUnchecked } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function Emprestimos({ user }) {
  const [emprestimos, setEmprestimos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [mensagem, setMensagem] = useState({ tipo: '', texto: '' });
  const [editandoId, setEditandoId] = useState(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [novoEmprestimo, setNovoEmprestimo] = useState({
    pessoa: '',
    descricao: '',
    valor: '',
    dataSaida: new Date().toISOString().split('T')[0],
    dataVencimento: '',
    status: 'pendente',
    juros: '',
    notas: '',
    numParcelas: 1
  });

  const [editarEmprestimo, setEditarEmprestimo] = useState({
    pessoa: '',
    descricao: '',
    valor: '',
    dataSaida: '',
    dataVencimento: '',
    status: 'pendente',
    juros: '',
    notas: '',
    valorRecebido: 0,
    parcelas: []
  });

  useEffect(() => {
    if (user && user.uid) {
      fetchEmprestimos();
    }
  }, [user]);

  const fetchEmprestimos = async () => {
    try {
      const q = query(collection(db, `users/${user.uid}/emprestimos`), orderBy('dataVencimento', 'desc'));
      const snap = await getDocs(q);
      setEmprestimos(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Erro ao buscar empréstimos:', error);
    }
  };

  const adicionarEmprestimo = async () => {
    if (!novoEmprestimo.pessoa || !novoEmprestimo.valor || !novoEmprestimo.dataVencimento) {
      setMensagem({ tipo: 'error', texto: 'Preencha pessoa, valor e data de vencimento!' });
      return;
    }

    try {
      const numParcelas = parseInt(novoEmprestimo.numParcelas) || 1;
      const valorParcela = parseFloat(novoEmprestimo.valor) / numParcelas;

      const parcelas = Array.from({ length: numParcelas }, (_, i) => ({
        numero: i + 1,
        valor: valorParcela,
        dataParcela: new Date(new Date(novoEmprestimo.dataVencimento).getTime() + (i * 30 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0],
        paga: false,
        dataPagamento: null
      }));

      await addDoc(collection(db, `users/${user.uid}/emprestimos`), {
        ...novoEmprestimo,
        valor: parseFloat(novoEmprestimo.valor),
        juros: novoEmprestimo.juros ? parseFloat(novoEmprestimo.juros) : 0,
        valorRecebido: 0,
        numParcelas: numParcelas,
        parcelas: parcelas,
        dataCriacao: new Date()
      });

      setNovoEmprestimo({
        pessoa: '',
        descricao: '',
        valor: '',
        dataSaida: new Date().toISOString().split('T')[0],
        dataVencimento: '',
        status: 'pendente',
        juros: '',
        notas: '',
        numParcelas: 1
      });

      setOpenDialog(false);
      setMensagem({ tipo: 'success', texto: 'Empréstimo adicionado com sucesso!' });
      fetchEmprestimos();
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
    } catch (error) {
      setMensagem({ tipo: 'error', texto: 'Erro ao adicionar empréstimo' });
    }
  };

  const abrirEdicao = (emp) => {
    setEditarEmprestimo(emp);
    setEditandoId(emp.id);
    setOpenEdit(true);
  };

  const salvarEdicao = async () => {
    try {
      // Calcular o novo status com base nas parcelas
      const parcelasPagas = editarEmprestimo.parcelas.filter(p => p.paga).length;
      let novoStatus = 'pendente';
      if (parcelasPagas === editarEmprestimo.parcelas.length) {
        novoStatus = 'recebido';
      } else if (parcelasPagas > 0) {
        novoStatus = 'parcial';
      }

      await updateDoc(doc(db, `users/${user.uid}/emprestimos/${editandoId}`), {
        ...editarEmprestimo,
        status: novoStatus, // Atualiza o status
        valor: parseFloat(editarEmprestimo.valor),
        juros: parseFloat(editarEmprestimo.juros) || 0,
        valorRecebido: parseFloat(editarEmprestimo.valorRecebido) || 0
      });

      setOpenEdit(false);
      setMensagem({ tipo: 'success', texto: 'Empréstimo atualizado com sucesso!' });
      fetchEmprestimos();
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
    } catch (error) {
      setMensagem({ tipo: 'error', texto: 'Erro ao atualizar empréstimo' });
    }
  };

  const deletarEmprestimo = async (id) => {
    if (window.confirm('Deseja deletar este empréstimo?')) {
      try {
        await deleteDoc(doc(db, `users/${user.uid}/emprestimos/${id}`));
        setMensagem({ tipo: 'success', texto: 'Empréstimo deletado!' });
        fetchEmprestimos();
        setTimeout(() => setMensagem({ tipo: '', texto: '' }), 3000);
      } catch (error) {
        setMensagem({ tipo: 'error', texto: 'Erro ao deletar' });
      }
    }
  };
  
  const marcarParcelaPaga = async (emprestimoId, parcelaIndex, paga) => {
    const emprestimoRef = doc(db, `users/${user.uid}/emprestimos`, emprestimoId);
    
    // Clonar o empréstimo para evitar mutação direta do estado
    const emprestimoAtualizado = { ...editarEmprestimo };
    const novasParcelas = [...emprestimoAtualizado.parcelas];
    
    // Atualizar a parcela específica
    novasParcelas[parcelaIndex] = {
      ...novasParcelas[parcelaIndex],
      paga: paga,
      dataPagamento: paga ? new Date().toISOString().split('T')[0] : null
    };
    
    emprestimoAtualizado.parcelas = novasParcelas;

    // Recalcular valor recebido e status
    const valorRecebido = novasParcelas.reduce((acc, p) => p.paga ? acc + p.valor : acc, 0);
    const parcelasPagasCount = novasParcelas.filter(p => p.paga).length;

    let novoStatus = 'pendente';
    if (parcelasPagasCount === novasParcelas.length) {
      novoStatus = 'recebido';
    } else if (parcelasPagasCount > 0) {
      novoStatus = 'parcial';
    }

    emprestimoAtualizado.status = novoStatus;
    emprestimoAtualizado.valorRecebido = valorRecebido;

    try {
      await updateDoc(emprestimoRef, {
        parcelas: novasParcelas,
        status: novoStatus,
        valorRecebido: valorRecebido
      });
      
      // Atualizar estado local para refletir a mudança na UI
      setEditarEmprestimo(emprestimoAtualizado);
      
      // Atualizar a lista principal de empréstimos
      fetchEmprestimos();
      setMensagem({ tipo: 'success', texto: `Parcela marcada como ${paga ? 'paga' : 'pendente'}!` });
      setTimeout(() => setMensagem({ tipo: '', texto: '' }), 2000);

    } catch (error) {
      console.error("Erro ao atualizar parcela:", error);
      setMensagem({ tipo: 'error', texto: 'Erro ao atualizar parcela.' });
    }
  };


  const calcularVencidos = () => emprestimos.filter(e => new Date(e.dataVencimento) < new Date() && e.status === 'pendente').length;
  const calcularTotal = () => emprestimos.reduce((sum, e) => sum + (e.valor || 0), 0);
  const calcularTotalRecebido = () => emprestimos.filter(e => e.status === 'recebido').reduce((sum, e) => sum + (e.valor || 0), 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pendente': return '#ff9800';
      case 'parcial': return '#2196f3';
      case 'recebido': return '#4caf50';
      default: return '#9e9e9e';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'parcial': return 'Parcial';
      case 'recebido': return 'Recebido';
      default: return status;
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, px: { xs: 1, sm: 2 }, mb: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate('/')} sx={{ mb: 2, color: '#1a237e', fontWeight: 'bold' }}>
        Voltar
      </Button>
      
      {/* ALERT */}
      {mensagem.texto && (
        <Alert severity={mensagem.tipo} sx={{ mb: 2 }} onClose={() => setMensagem({ tipo: '', texto: '' })}>
          {mensagem.texto}
        </Alert>
      )}

      {/* CARDS DE RESUMO */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e8f5e9', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: '#558b2f', fontSize: '0.75rem' }}>
              A RECEBER
            </Typography>
            <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 'bold' }}>
              R$ {calcularTotal().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#fff3e0', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: '#e65100', fontSize: '0.75rem' }}>
              PENDENTES
            </Typography>
            <Typography variant="h6" sx={{ color: '#ff9800', fontWeight: 'bold' }}>
              {emprestimos.filter(e => e.status === 'pendente').length}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#ffebee', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: '#b71c1c', fontSize: '0.75rem' }}>
              VENCIDOS
            </Typography>
            <Typography variant="h6" sx={{ color: '#f44336', fontWeight: 'bold' }}>
              {calcularVencidos()}
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 1.5, textAlign: 'center', bgcolor: '#e0f2f1', borderRadius: 2 }}>
            <Typography variant="caption" sx={{ color: '#004d40', fontSize: '0.75rem' }}>
              RECEBIDO
            </Typography>
            <Typography variant="h6" sx={{ color: '#009688', fontWeight: 'bold' }}>
              R$ {calcularTotalRecebido().toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* BOTÃO ADICIONAR */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenDialog(true)}
          sx={{ bgcolor: '#1a237e', textTransform: 'none' }}
          fullWidth={window.innerWidth < 600}
        >
          Novo Empréstimo
        </Button>
      </Box>

      {/* TABELA RESPONSIVA */}
      {emprestimos.length > 0 ? (
        isMobile ? (
          <Grid container spacing={2}>
            {emprestimos.map(emp => (
              <Grid item xs={12} key={emp.id}>
                <Card sx={{ borderRadius: 2, border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" fontWeight="bold">{emp.pessoa}</Typography>
                      <Chip
                        label={getStatusLabel(emp.status)}
                        size="small"
                        sx={{ bgcolor: getStatusColor(emp.status), color: 'white', fontWeight: 'bold' }}
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{emp.descricao}</Typography>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="body2">Valor:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="#2e7d32">
                        R$ {(emp.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Vencimento:</Typography>
                      <Typography variant="body2">{new Date(emp.dataVencimento).toLocaleDateString('pt-BR')}</Typography>
                    </Box>
                    {new Date(emp.dataVencimento) < new Date() && emp.status === 'pendente' && (
                      <Typography variant="caption" sx={{ display: 'block', color: '#f44336', fontWeight: 'bold', textAlign: 'right' }}>
                        VENCIDO
                      </Typography>
                    )}
                  </CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1, gap: 1, borderTop: '1px solid #e0e0e0' }}>
                    <Button
                      size="small"
                      startIcon={<Edit />}
                      onClick={() => abrirEdicao(emp)}
                      sx={{ color: '#1a237e' }}
                    >
                      Editar
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Delete />}
                      onClick={() => deletarEmprestimo(emp.id)}
                      color="error"
                    >
                      Deletar
                    </Button>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead sx={{ bgcolor: '#1a237e' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>Pessoa</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>Valor</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>Vencimento</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>Status</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.8rem' }}>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {emprestimos.map((emp) => (
                  <TableRow key={emp.id} sx={{ '&:hover': { bgcolor: '#f5f5f5' }, bgcolor: emp.status === 'recebido' ? '#e8f5e9' : 'transparent' }}>
                    <TableCell sx={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
                      {emp.pessoa}
                      {emp.descricao && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#666' }}>
                          {emp.descricao}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                      R$ {(emp.valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                      {new Date(emp.dataVencimento).toLocaleDateString('pt-BR')}
                      {new Date(emp.dataVencimento) < new Date() && emp.status === 'pendente' && (
                        <Typography variant="caption" sx={{ display: 'block', color: '#f44336', fontWeight: 'bold' }}>
                          VENCIDO
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={getStatusLabel(emp.status)}
                        size="small"
                        sx={{
                          bgcolor: getStatusColor(emp.status),
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', flexDirection: { xs: 'column', sm: 'row' } }}>
                        <IconButton
                          size="small"
                          onClick={() => abrirEdicao(emp)}
                          sx={{ color: '#1a237e' }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => deletarEmprestimo(emp.id)}
                          sx={{ color: '#d32f2f' }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
        )
      ) : (
        <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
          <Typography color="textSecondary">Nenhum empréstimo cadastrado</Typography>
        </Paper>
      )}

      {/* DIALOG ADICIONAR */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 'bold' }}>
          <Add sx={{ mr: 1, verticalAlign: 'middle' }} />
          Novo Empréstimo
        </DialogTitle>
        <DialogContent sx={{ mt: 2, maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Pessoa"
                placeholder="Ex: João Silva"
                value={novoEmprestimo.pessoa}
                onChange={(e) => setNovoEmprestimo({ ...novoEmprestimo, pessoa: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                placeholder="Ex: Empréstimo para carro"
                value={novoEmprestimo.descricao}
                onChange={(e) => setNovoEmprestimo({ ...novoEmprestimo, descricao: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Valor"
                type="number"
                inputProps={{ step: '0.01' }}
                value={novoEmprestimo.valor}
                onChange={(e) => setNovoEmprestimo({ ...novoEmprestimo, valor: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Parcelas"
                type="number"
                inputProps={{ min: 1, step: 1 }}
                value={novoEmprestimo.numParcelas}
                onChange={(e) => setNovoEmprestimo({ ...novoEmprestimo, numParcelas: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Juros (%)"
                type="number"
                inputProps={{ step: '0.01' }}
                value={novoEmprestimo.juros}
                onChange={(e) => setNovoEmprestimo({ ...novoEmprestimo, juros: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Data de Saída"
                type="date"
                value={novoEmprestimo.dataSaida}
                onChange={(e) => setNovoEmprestimo({ ...novoEmprestimo, dataSaida: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Data de Vencimento"
                type="date"
                value={novoEmprestimo.dataVencimento}
                onChange={(e) => setNovoEmprestimo({ ...novoEmprestimo, dataVencimento: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas"
                multiline
                rows={2}
                placeholder="Adicione observações..."
                value={novoEmprestimo.notas}
                onChange={(e) => setNovoEmprestimo({ ...novoEmprestimo, notas: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button onClick={adicionarEmprestimo} variant="contained" sx={{ bgcolor: '#1a237e' }}>
            Adicionar
          </Button>
        </DialogActions>
      </Dialog>

      {/* DIALOG EDITAR */}
      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { maxHeight: '90vh' } }}>
        <DialogTitle sx={{ bgcolor: '#1a237e', color: 'white', fontWeight: 'bold' }}>
          <Edit sx={{ mr: 1, verticalAlign: 'middle' }} />
          Editar Empréstimo
        </DialogTitle>
        <DialogContent sx={{ mt: 2, maxHeight: 'calc(90vh - 140px)', overflowY: 'auto' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nome da Pessoa"
                value={editarEmprestimo.pessoa}
                onChange={(e) => setEditarEmprestimo({ ...editarEmprestimo, pessoa: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={editarEmprestimo.descricao}
                onChange={(e) => setEditarEmprestimo({ ...editarEmprestimo, descricao: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Valor Original"
                type="number"
                inputProps={{ step: '0.01' }}
                value={editarEmprestimo.valor}
                onChange={(e) => setEditarEmprestimo({ ...editarEmprestimo, valor: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Valor Recebido"
                type="number"
                inputProps={{ step: '0.01', readOnly: true }} // Tornar campo somente leitura
                value={editarEmprestimo.valorRecebido || 0}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Data de Saída"
                type="date"
                value={editarEmprestimo.dataSaida}
                onChange={(e) => setEditarEmprestimo({ ...editarEmprestimo, dataSaida: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Data de Vencimento"
                type="date"
                value={editarEmprestimo.dataVencimento}
                onChange={(e) => setEditarEmprestimo({ ...editarEmprestimo, dataVencimento: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                label="Status"
                value={editarEmprestimo.status}
                onChange={(e) => setEditarEmprestimo({ ...editarEmprestimo, status: e.target.value })}
                SelectProps={{
                  native: true,
                }}
                InputLabelProps={{ shrink: true }}
              >
                <option value="pendente">Pendente</option>
                <option value="parcial">Parcial</option>
                <option value="recebido">Recebido</option>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notas"
                multiline
                rows={2}
                value={editarEmprestimo.notas}
                onChange={(e) => setEditarEmprestimo({ ...editarEmprestimo, notas: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* MOSTRAR PARCELAS SE EXISTIR */}
            {editarEmprestimo.parcelas && editarEmprestimo.parcelas.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5, color: '#1a237e' }}>
                  📋 Parcelas ({editarEmprestimo.parcelas.filter(p => p.paga).length}/{editarEmprestimo.parcelas.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {editarEmprestimo.parcelas.map((parcela, idx) => (
                    <Box key={idx} sx={{ p: 1, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #ddd', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          Parcela {parcela.numero}: R$ {parcela.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Vencimento: {new Date(parcela.dataParcela).toLocaleDateString('pt-BR')}
                          {parcela.paga && ` • Pago em: ${new Date(parcela.dataPagamento).toLocaleDateString('pt-BR')}`}
                        </Typography>
                      </Box>
                      <Button
                        variant={parcela.paga ? "contained" : "outlined"}
                        size="small"
                        color={parcela.paga ? "success" : "warning"}
                        startIcon={parcela.paga ? <CheckCircleOutline /> : <RadioButtonUnchecked />}
                        onClick={() => marcarParcelaPaga(editandoId, idx, !parcela.paga)}
                        sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                      >
                        {parcela.paga ? 'Paga' : 'Pagar'}
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => deletarEmprestimo(editandoId)}
            color="error"
            sx={{ mr: 'auto' }}
          >
            <Delete sx={{ mr: 0.5 }} /> Deletar
          </Button>
          <Button onClick={() => setOpenEdit(false)}>Cancelar</Button>
          <Button onClick={salvarEdicao} variant="contained" sx={{ bgcolor: '#1a237e' }}>
            Salvar e Fechar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default Emprestimos;
